import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  IRecommendation,
  IRecommendationService,
  IUnsavedRecommendationService,
} from '../../utils/types';

export interface RecommendationState {
  treatmentPlanId: number | null;
  recommendation: IRecommendation | null;
  services: IRecommendationService[];
  error: any;
  loading: boolean;
  errorState: any;
  unsavedServices: IUnsavedRecommendationService[];
}

type RecommendationStateUpdate = {
  [Key in keyof RecommendationState]: { key: Key; data: RecommendationState[Key] };
}[keyof RecommendationState];

const name = 'treatmentRecommendation';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: These legacy thunk payloads are untyped at their API boundary.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<RecommendationState>) =>
    createExtraReducers(builder),
});

function createInitialState(): RecommendationState {
  return {
    treatmentPlanId: null,
    recommendation: null,
    services: [],
    error: null,
    loading: false,
    errorState: {},
    unsavedServices: [],
  };
}

function createReducers() {
  return {
    resetState,
    updateState,
    updateErrorState,
  };

  function updateState(
    state: RecommendationState,
    action: PayloadAction<RecommendationStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
  function resetState(state: RecommendationState) {
    state.treatmentPlanId = null;
    state.recommendation = null;
    state.services = [];
    state.unsavedServices = [];
  }
  function updateErrorState(state: RecommendationState, action: any) {
    state.errorState = { ...state.errorState, ...action.payload };
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getRecommendation: getRecommendation(),
    createRecommendation: createRecommendation(),
    removeRecommendation: removeRecommendation(),
    updateRecommendation: updateRecommendation(),
    createService: createService(),
    removeService: removeService(),
    updateService: updateService(),
  };

  function getRecommendation() {
    return createAsyncThunk(
      `${name}/getRecommendation`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function createRecommendation() {
    return createAsyncThunk(
      `${name}/createRecommendation`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }
  function removeRecommendation() {
    return createAsyncThunk(
      `${name}/removeRecommendation`,
      async ({ getAccessTokenSilently, treatmentPlanId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function updateRecommendation() {
    return createAsyncThunk(
      `${name}/updateRecommendation`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }

  function createService() {
    return createAsyncThunk(
      `${name}/createService`,
      async ({ treatmentPlanId, treatmentRecommendationId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/${treatmentRecommendationId}/services/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
  function removeService() {
    return createAsyncThunk(
      `${name}/removeService`,
      async ({
        treatmentPlanId,
        treatmentRecommendationId,
        serviceId,
        getAccessTokenSilently,
      }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/${treatmentRecommendationId}/services/${serviceId}/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function updateService() {
    return createAsyncThunk(
      `${name}/updateService`,
      async ({ treatmentPlanId, treatmentRecommendationId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/treatment_recommendations/${treatmentRecommendationId}/services/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<RecommendationState>) {
  const {
    getRecommendation,
    createRecommendation,
    removeRecommendation,
    updateRecommendation,
    createService,
    removeService,
    updateService,
  } = extraActions;
  builder
    // getRecommendation()
    .addCase(getRecommendation.pending, (state: RecommendationState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getRecommendation.fulfilled,
      (
        state: RecommendationState,
        action: PayloadAction<IRecommendation, string, { arg: { forceUpdate: boolean } }>,
      ) => {
        const { services } = action.payload;
        state.loading = false;
        if (
          action.meta.arg.forceUpdate ||
          (action.payload &&
            Object.keys(action.payload).length !== 0 &&
            (!state.recommendation || Object.keys(state.recommendation).length === 0))
        ) {
          state.recommendation = action.payload;
        }

        state.services = services ? services : [];
      },
    )
    .addCase(getRecommendation.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createRecommendation()
    .addCase(createRecommendation.pending, (state: RecommendationState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createRecommendation.fulfilled,
      (state: RecommendationState, action: PayloadAction<IRecommendation>) => {
        state.recommendation = action.payload;
        state.loading = false;
        state.unsavedServices = state.unsavedServices.map(
          (service: IUnsavedRecommendationService) => ({
            ...service,
            recommendationId: action.payload.id,
          }),
        );
      },
    )
    .addCase(createRecommendation.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // removeRecommendation()
    .addCase(removeRecommendation.pending, (state: RecommendationState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeRecommendation.fulfilled, (state: RecommendationState) => {
      state.recommendation = null;
      state.loading = false;
    })
    .addCase(removeRecommendation.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateRecommendation()
    .addCase(updateRecommendation.pending, (state: RecommendationState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateRecommendation.fulfilled,
      (state: RecommendationState, action: PayloadAction<IRecommendation>) => {
        state.recommendation = action.payload;
        state.loading = false;
      },
    )
    .addCase(updateRecommendation.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createService()
    .addCase(createService.pending, (state: RecommendationState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      createService.fulfilled,
      (state: RecommendationState, action: PayloadAction<IRecommendationService>) => {
        // mix this value into the existing state
        state.services = [...state.services, action.payload];
        state.loading = false;
      },
    )
    .addCase(createService.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // removeService()
    .addCase(removeService.pending, (state: RecommendationState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      removeService.fulfilled,
      (
        state: RecommendationState,
        action: PayloadAction<IRecommendationService, string, { arg: { serviceId: number } }>,
      ) => {
        state.services = state.services.filter(
          (service) => service.id !== action.meta.arg.serviceId,
        );
        state.loading = false;
      },
    )
    .addCase(removeService.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateServices()
    .addCase(updateService.pending, (state: RecommendationState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateService.fulfilled,
      (state: RecommendationState, action: PayloadAction<IRecommendationService>) => {
        state.services = [...state.services, action.payload];
        state.loading = false;
      },
    )
    .addCase(updateService.rejected, (state: RecommendationState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const treatmentRecommendationActions = { ...slice.actions, ...extraActions };
export const treatmentRecommendationReducer = slice.reducer;
